import Cookies from 'js-cookie';
import { useClickOutside } from 'stimulus-use';
import { EMAIL_REGEX } from '../constants/regex';
import { IUserInfo } from '../models/user';
import { Controller } from '../utils/controller';
import getPerlgemCSRFToken from '../utils/perlgem_csrf';

interface FormDataParams {
  _SUBMIT: 'email_signup' | 'loyalty_email_signup' | 'loyalty_registration';
  COUNTRY_ID: string;
  LANGUAGE_ID: string;
  LOYALTY_ACTIVE_FLAG: string;
  ACCEPTED_LOYALTY_TERMS: string;
  LAST_SOURCE: string;
  LOYALTY_ENROLLED_SOURCE: string;
  redirect_or_text: string;
  redirect: string;
  PC_EMAIL_ADDRESS: string;
  PC_EMAIL_PROMOTIONS: string;
  PC_EMAIL_PROMOTIONS_PRESENT: string;
  _TOKEN: string;
}

export default class extends Controller {
  static targets = [
    'email',
    'emailChecked',
    'emailError',
    'modalOverlay',
    'signupButton',
    'signup',
    'accountCreate',
    'accountCreateWithPromo',
    'accountHolderUnusedSuccess',
    'accountHolderUsedSuccess'
  ];

  static values = {
    displayOverlayOnHover: {
      type: Boolean,
      default: false
    },
    dontShowAgainForNumberOfDays: {
      type: Number,
      default: 0
    },
    keepOverlayOpen: {
      type: Boolean,
      default: false
    },
    displayOverlayOnPageLoad: {
      type: Boolean,
      default: false
    },
    cookie: {
      type: String,
      default: ''
    },
    modalCloseTimeout: {
      type: Number,
      default: 0
    },
    requireTermsAcceptance: {
      type: Boolean,
      default: false
    },
    emailErrorMessage: {
      type: String,
      default: 'Please enter your email address in the following format: jane@aol.com'
    },
    emailErrorCheckboxMessage: {
      type: String,
      default: 'Please accept Terms & Conditions'
    }
  };

  static classes = ['position', 'centeredPosition'];

  // TARGETS
  declare emailTarget: HTMLInputElement;
  declare emailCheckedTarget: HTMLInputElement;
  declare emailErrorTarget: HTMLElement;
  declare signupButtonTarget: HTMLButtonElement;

  declare modalOverlayTarget: HTMLDivElement;
  declare signupTarget: HTMLElement;
  declare accountCreateTarget: HTMLElement;
  declare accountCreateWithPromoTarget: HTMLElement;
  declare accountHolderUnusedSuccessTarget: HTMLElement;
  declare accountHolderUsedSuccessTarget: HTMLElement;

  declare readonly hasModalOverlayTarget: boolean;
  declare readonly hasEmailTarget: boolean;
  declare readonly hasEmailCheckedTarget: boolean;
  declare readonly hasEmailErrorTarget: boolean;
  declare readonly hasSignupButtonTarget: boolean;

  declare readonly hasSignupTarget: boolean;
  declare readonly hasAccountCreateTarget: boolean;
  declare readonly hasAccountCreateWithPromoTarget: boolean;
  declare readonly hasAccountHolderUnusedSuccessTarget: boolean;
  declare readonly hasAccountHolderUsedSuccessTarget: boolean;

  // VALUES
  declare displayOverlayOnPageLoadValue: boolean;
  declare displayOverlayOnHoverValue: boolean;
  declare dontShowAgainForNumberOfDaysValue: number;
  declare keepOverlayOpenValue: boolean;
  declare cookieValue: number;
  declare modalCloseTimeoutValue: number;
  declare requireTermsAcceptanceValue: boolean;
  declare emailErrorMessageValue: string;
  declare emailErrorCheckboxMessageValue: string;

  // CLASSES
  declare positionClasses: string[];
  declare centeredPositionClasses: string[];

  // LOCAL STATE
  private visited: boolean;
  private hideClass: string;
  private cursorNotAllowedClass: string;
  private timeoutRef: NodeJS.Timeout;

  connect() {
    this.hasModalOverlayTarget && useClickOutside(this, { element: this.modalOverlayTarget });
  }

  initialize() {
    this.visited = false;
    this.hideClass = 'hidden';
    this.cursorNotAllowedClass = 'cursor-not-allowed';

    if (this.displayOverlayOnPageLoadValue) {
      if (this.cookieValue && !Cookies.get(this.cookieValue)) {
        this.setCookie();
        this.showOverlay();
      }
      if (this.modalCloseTimeoutValue) {
        this.closeOverlayByTime(this.modalCloseTimeoutValue);
      }
    }
  }

  setCookie = () => {
    if (this.cookieValue) {
      const options: Record<string, unknown> = {
        path: '/'
      };
      if (this.dontShowAgainForNumberOfDaysValue) {
        options.expires = this.dontShowAgainForNumberOfDaysValue;
      }
      Cookies.set(this.cookieValue, '1', options);
    }
  };

  hideOverlay = () => {
    // email
    this.hideTarget(this.hasEmailErrorTarget, this.emailErrorTarget);
    if (this.hasEmailTarget) {
      this.emailTarget.value = '';
    }
    if (this.hasEmailCheckedTarget) {
      this.emailCheckedTarget.checked = false;
    }
    // modals
    this.removeClasses(this.hasModalOverlayTarget, this.modalOverlayTarget, this.positionClasses);
    this.removeClasses(
      this.hasModalOverlayTarget,
      this.modalOverlayTarget,
      this.centeredPositionClasses
    );
    this.showTarget(this.hasSignupTarget, this.signupTarget);
    this.hideTarget(this.hasModalOverlayTarget, this.modalOverlayTarget);
    this.hideTarget(
      this.hasAccountHolderUnusedSuccessTarget,
      this.accountHolderUnusedSuccessTarget
    );
    this.hideTarget(this.hasAccountHolderUsedSuccessTarget, this.accountHolderUsedSuccessTarget);
    this.clearTarget(this.hasAccountCreateTarget, this.accountCreateTarget);
    this.clearTarget(this.hasAccountCreateWithPromoTarget, this.accountCreateWithPromoTarget);
  };

  showOverlay = (centered = false) => {
    const serviceError = document.querySelector<HTMLElement>('[data-behavior="serviceError"]');
    const positionClasses = centered ? this.centeredPositionClasses : this.positionClasses;
    this.showTarget(this.hasModalOverlayTarget, this.modalOverlayTarget);
    this.addClasses(this.hasModalOverlayTarget, this.modalOverlayTarget, positionClasses);
    this.hideTarget(!!serviceError, serviceError);
  };

  closeOverlayByTime = (timeout: number) => {
    if (timeout) {
      clearTimeout(this.timeoutRef);
      this.timeoutRef = setTimeout(this.hideOverlay, timeout * 1000);
    }
  };

  closeOutside(event: Event) {
    event.preventDefault();
    this.closeOverlayByTime(0.25);
  }

  onClickCloseOverlay() {
    this.setCookie();
    this.closeOverlayByTime(0.1);
  }

  onClickOverlayToggle(event) {
    if (this.hasModalOverlayTarget) {
      const { centered } = event.params;
      if (this.modalOverlayTarget.classList.contains('hidden')) {
        this.showOverlay(centered);
      } else {
        this.hideOverlay();
      }
    }
  }

  onMouseEnterShowOverlay() {
    if (this.displayOverlayOnHoverValue) {
      this.showOverlay();
    }
  }

  onMouseEnter() {
    this.visited = true;
  }

  onMouseLeave() {
    if (this.displayOverlayOnHoverValue && !this.keepOverlayOpenValue && this.visited) {
      this.closeOverlayByTime(0.5);
      this.visited = false;
    }
  }

  formDataJSONRPC(params: Partial<FormDataParams>, formData = new URLSearchParams()) {
    formData.append(
      'JSONRPC',
      JSON.stringify([
        {
          method: 'rpc.form',
          params: [{ ...params }]
        }
      ])
    );
    return formData;
  }

  async submitJSONRPC(formData: URLSearchParams, endpoint: string) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        mode: 'cors'
      });

      const json = await response.json();

      return json;
    } catch {
      const serviceError = document.querySelector<HTMLElement>('[data-behavior="serviceError"]');

      if (serviceError) {
        this.clearTarget(this.hasAccountCreateTarget, this.accountCreateTarget);
        this.clearTarget(this.hasAccountCreateWithPromoTarget, this.accountCreateWithPromoTarget);
        this.showTarget(!!serviceError, serviceError);
      }
    } finally {
      this.enableButton(this.hasSignupButtonTarget, this.signupButtonTarget);
    }
  }

  async emailSignUp(event: Event) {
    try {
      event.preventDefault();

      const checked = this.hasEmailCheckedTarget ? this.emailCheckedTarget.checked : false;
      const email = this.hasEmailTarget ? this.emailTarget.value.trim() : '';
      const validEmail = this.isValidEmail(email);

      if (!validEmail || (!checked && this.requireTermsAcceptanceValue)) {
        const emailError = !validEmail ? `<p>${this.emailErrorMessageValue}</p>` : '';
        const emailCheckedError =
          !checked && this.requireTermsAcceptanceValue
            ? `<p>${this.emailErrorCheckboxMessageValue}</p>`
            : '';

        this.emailErrorTarget.innerHTML = `${emailError}${emailCheckedError}`;

        if (!validEmail) {
          this.emailTarget.focus();
        }

        this.showTarget(this.hasEmailErrorTarget, this.emailErrorTarget);

        return;
      }

      this.hideTarget(this.hasEmailErrorTarget, this.emailErrorTarget);
      this.disableButton(this.hasSignupButtonTarget, this.signupButtonTarget);

      const token = await getPerlgemCSRFToken();
      const formData = this.formDataJSONRPC({
        _SUBMIT: 'loyalty_email_signup',
        PC_EMAIL_ADDRESS: email,
        PC_EMAIL_PROMOTIONS: '1',
        PC_EMAIL_PROMOTIONS_PRESENT: '1',
        ACCEPTED_LOYALTY_TERMS: '1',
        LAST_SOURCE: 'elist15',
        LOYALTY_ENROLLED_SOURCE: 'SITE_elist15',
        LOYALTY_ACTIVE_FLAG: '1',
        _TOKEN: token
      });

      const [response] = await this.submitJSONRPC(formData, '/rpc/jsonrpc.tmpl?dbgmethod=rpc.form');

      if (response?.error) {
        this.hideTarget(this.hasEmailErrorTarget, this.emailErrorTarget);
        return;
      }

      const { userinfo }: { userinfo: IUserInfo } = response.result.data;

      const isUserRegistered = this.isUserRegistered(userinfo.registered_user);
      const userHasSpecialOffer = this.userHasSpecialOffer(userinfo.special_offer_code_status);
      const userHasPromotion = this.userHasPromotion(userinfo.previously_opted_in_email_promotion);

      this.hideTarget(this.hasSignupTarget, this.signupTarget);

      if (isUserRegistered) {
        if (userHasSpecialOffer) {
          this.showTarget(
            this.hasAccountHolderUnusedSuccessTarget,
            this.accountHolderUnusedSuccessTarget
          );
          return;
        }

        this.showTarget(
          this.hasAccountHolderUsedSuccessTarget,
          this.accountHolderUsedSuccessTarget
        );

        return;
      }

      if (userHasPromotion) {
        this.showTarget(this.hasAccountCreateWithPromoTarget, this.accountCreateWithPromoTarget);
        return;
      }

      this.showTarget(this.hasAccountCreateTarget, this.accountCreateTarget);
    } catch {
      const serviceError = document.querySelector<HTMLElement>('[data-behavior="serviceError"]');

      if (serviceError) {
        this.hideTarget(this.hasSignupTarget, this.signupTarget);
        this.showTarget(!!serviceError, serviceError);
      }
    } finally {
      this.enableButton(this.hasSignupButtonTarget, this.signupButtonTarget);
    }
  }

  disableButton(hasTarget: boolean, target: HTMLButtonElement) {
    if (hasTarget) {
      target.classList.add(this.cursorNotAllowedClass);
      target.setAttribute('disabled', '');
    }
  }

  enableButton(hasTarget: boolean, target: HTMLButtonElement) {
    if (hasTarget) {
      target.classList.remove(this.cursorNotAllowedClass);
      target.removeAttribute('disabled');
    }
  }

  addClasses(hasTarget: boolean, target: HTMLElement | null, classes: string[]) {
    hasTarget && target?.classList?.add(...classes);
  }

  removeClasses(hasTarget: boolean, target: HTMLElement | null, classes: string[]) {
    hasTarget && target?.classList?.remove(...classes);
  }

  removeClass(hasTarget: boolean, target: HTMLElement, classes: string[]) {
    hasTarget && target?.classList?.add(...classes);
  }

  showTarget(hasTarget: boolean, target: HTMLElement | null) {
    this.removeClasses(hasTarget, target, [this.hideClass]);
  }

  hideTarget(hasTarget: boolean, target: HTMLElement | null) {
    this.addClasses(hasTarget, target, [this.hideClass]);
  }

  clearTarget(hasTarget: boolean, target: HTMLElement) {
    this.hideTarget(hasTarget, target);
    const firstName = target.querySelector<HTMLInputElement>('[data-behavior="firstName"]');
    const firstNameError = target.querySelector<HTMLDivElement>('[data-behavior="firstNameError"]');
    const password = target.querySelector<HTMLInputElement>('[data-behavior="password"]');

    if (firstNameError) {
      firstNameError.classList.add(this.hideClass);
    }
    if (firstName) {
      firstName.value = '';
    }
    if (password) {
      password.value = '';
    }
  }

  isValidEmail(email: string) {
    return EMAIL_REGEX.test(email);
  }

  isUserRegistered(registered_user: number) {
    return registered_user === 1;
  }

  userHasSpecialOffer(special_offer_code_status: number) {
    return special_offer_code_status === 0;
  }

  userHasPromotion(previously_opted_in_email_promotion: number) {
    return previously_opted_in_email_promotion === 1;
  }
}
